/**
 * Contentful Images API helpers for the CMS Image model.
 *
 * Builds transform URLs (`w`, `h`, `q`, `fm`, `fit`, `f`) and responsive markup
 * (`srcset` / `sizes`) per the TSD Responsive Image Params:
 * https://amblique.atlassian.net/wiki/spaces/KL/pages/97091602#Responsive-Image-Params
 *
 * TSD helpers `sw` (srcset widths) and `sm` (DPR multipliers) are **frontend**
 * conventions — Contentful's Images API does not honour those query params.
 * We expand them into a real HTML `srcset` of `?w=` candidates (browser picks by
 * viewport + DPR via `w` descriptors).
 *
 * @see https://www.contentful.com/developers/docs/references/images-api/
 */

/** Default quality when the CMS Quality field is blank (TSD suggestion). */
export const DEFAULT_CONTENTFUL_QUALITY = 70;

/** Default format when the CMS Format field is blank (KL-1133 / WebP). */
export const DEFAULT_CONTENTFUL_FORMAT = 'webp';

/**
 * Default srcset width ladder (TSD `sw` example) used when the author has not
 * set Width and has not supplied an explicit srcset-widths value.
 */
export const DEFAULT_SRCSET_WIDTHS = [320, 640, 960, 1280, 1600, 1920];

/**
 * Default DPR multipliers (TSD `sm=1,2,3`). Applied to base widths so retina
 * candidates exist in the `srcset`.
 */
export const DEFAULT_SRCSET_MULTIPLIERS = [1, 2, 3];

/** Contentful Images API rejects transforms above this edge length. */
export const CONTENTFUL_MAX_DIMENSION = 4000;
/**
 * Normalise Contentful asset URLs so `URL` can parse them.
 * Delivery often returns protocol-relative `//images.ctfassets.net/...`.
 *
 * @param {string} imageUrl
 * @returns {string}
 */
export function normalizeContentfulAssetUrl(imageUrl) {
  if (!imageUrl || typeof imageUrl !== 'string') {
    return '';
  }

  if (imageUrl.startsWith('//')) {
    return `https:${imageUrl}`;
  }

  return imageUrl;
}

/**
 * Parse a comma-separated list of positive numbers (e.g. `"320,640,960"` or
 * responsive shorthand leftovers). Invalid tokens are dropped.
 *
 * @param {unknown} value
 * @returns {number[]}
 */
export function parseNumberList(value) {
  if (value == null || value === '') {
    return [];
  }

  if (Array.isArray(value)) {
    return value
      .map((n) => Number(n))
      .filter((n) => Number.isFinite(n) && n > 0)
      .map((n) => Math.round(n));
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? [Math.round(value)] : [];
  }

  if (typeof value !== 'string') {
    return [];
  }

  return value
    .split(/[,;\s]+/)
    .map((token) => Number(token.replace(/[^\d.]/g, '')))
    .filter((n) => Number.isFinite(n) && n > 0)
    .map((n) => Math.round(n));
}

/**
 * Collect numeric widths from a `useResponsiveValue(..., 'device')` result or
 * any object / scalar width value.
 *
 * @param {unknown} widthValue
 * @returns {number[]}
 */
export function collectAuthoredWidths(widthValue) {
  if (widthValue == null || widthValue === '') {
    return [];
  }

  if (typeof widthValue === 'number') {
    return parseNumberList(widthValue);
  }

  if (typeof widthValue === 'string') {
    // Strip named descriptors (`mobile: 750`) then parse remaining numbers.
    const stripped = widthValue.replace(/\b(?:mobile|tablet|desktop)\s*:/gi, '');
    return parseNumberList(stripped);
  }

  if (typeof widthValue === 'object') {
    const keys = ['mobile', 'tablet', 'desktop', 'base', 'sm', 'md', 'lg', 'xl', '2xl', 'value'];
    const nums = [];

    keys.forEach((key) => {
      const n = Number(widthValue[key]);
      if (Number.isFinite(n) && n > 0) {
        nums.push(Math.round(n));
      }
    });

    return [...new Set(nums)];
  }

  return [];
}

/**
 * Resolve the final list of pixel widths for `srcset` candidates.
 *
 * Priority:
 * 1. Explicit `sw` / `srcSetWidths` (CMS or prop)
 * 2. Authored Width field values (all breakpoints)
 * 3. Current display width
 * 4. Default ladder
 *
 * Then expand by `sm` / `srcSetMultipliers` (default 1×/2×/3×) and cap at the
 * asset's intrinsic width when known.
 *
 * @param {Object} options
 * @param {unknown} [options.srcSetWidths] - TSD `sw`
 * @param {unknown} [options.srcSetMultipliers] - TSD `sm`
 * @param {unknown} [options.authoredWidth] - CMS Width (string/object/number)
 * @param {number} [options.displayWidth] - Current breakpoint width
 * @param {number} [options.maxWidth] - Intrinsic asset width cap
 * @returns {number[]}
 */
export function resolveSrcSetWidths({
  srcSetWidths,
  srcSetMultipliers,
  authoredWidth,
  displayWidth,
  maxWidth,
} = {}) {
  let bases = parseNumberList(srcSetWidths);

  if (bases.length === 0) {
    bases = collectAuthoredWidths(authoredWidth);
  }

  if (bases.length === 0 && Number.isFinite(displayWidth) && displayWidth > 0) {
    bases = [Math.round(displayWidth)];
  }

  if (bases.length === 0) {
    bases = DEFAULT_SRCSET_WIDTHS.slice();
  }

  const multipliers = parseNumberList(srcSetMultipliers);
  const sm = multipliers.length > 0 ? multipliers : DEFAULT_SRCSET_MULTIPLIERS;

  const expanded = new Set();
  bases.forEach((w) => {
    sm.forEach((m) => {
      const candidate = Math.round(w * m);
      if (candidate > 0) {
        expanded.add(candidate);
      }
    });
  });

  const hardCap =
    Number.isFinite(maxWidth) && maxWidth > 0
      ? Math.min(Math.round(maxWidth), CONTENTFUL_MAX_DIMENSION)
      : CONTENTFUL_MAX_DIMENSION;

  let widths = [...expanded]
    .filter((w) => w <= hardCap)
    .sort((a, b) => a - b);

  // Always keep at least one candidate at the cap when everything was filtered out.
  if (widths.length === 0) {
    widths = [hardCap];
  }

  return widths;
}

/**
 * Build a single Contentful Images API URL.
 *
 * @param {string} imageUrl
 * @param {Object} [params]
 * @param {number|string} [params.w]
 * @param {number|string} [params.h]
 * @param {number|string} [params.q]
 * @param {string} [params.fm]
 * @param {string} [params.fit]
 * @param {string} [params.f]
 * @returns {string}
 */
export function buildContentfulImageUrl(imageUrl, params = {}) {
  const normalized = normalizeContentfulAssetUrl(imageUrl);
  if (!normalized) {
    return '';
  }

  let url;
  try {
    url = new URL(normalized);
  } catch {
    return imageUrl;
  }

  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      url.searchParams.set(key, String(value));
    }
  });

  return url.toString();
}

/**
 * Build `src`, `srcSet`, and `sizes` for a Contentful Image entry.
 *
 * @param {Object} options
 * @param {string} options.imageUrl - Asset URL for the current breakpoint
 * @param {number} [options.width] - Display width (current breakpoint)
 * @param {number} [options.height] - Display height (current breakpoint)
 * @param {unknown} [options.quality] - CMS quality (defaults to 70)
 * @param {string} [options.format] - CMS format (defaults to webp)
 * @param {string} [options.fit]
 * @param {string} [options.focus] - Already tableized focus value (`face`, `top_left`, …)
 * @param {unknown} [options.authoredWidth] - Raw CMS Width field
 * @param {unknown} [options.srcSetWidths] - Optional TSD `sw` override
 * @param {unknown} [options.srcSetMultipliers] - Optional TSD `sm` override
 * @param {string} [options.sizes] - Optional `sizes` override
 * @param {number} [options.maxWidth] - Intrinsic asset width
 * @returns {{src: string, srcSet: string, sizes: string}}
 */
export function buildContentfulImageAttributes({
  imageUrl,
  width,
  height,
  quality,
  format,
  fit,
  focus,
  authoredWidth,
  srcSetWidths,
  srcSetMultipliers,
  sizes: sizesOverride,
  maxWidth,
} = {}) {
  if (!imageUrl) {
    return {src: '', srcSet: '', sizes: ''};
  }

  const q =
    quality !== null && quality !== undefined && quality !== ''
      ? quality
      : DEFAULT_CONTENTFUL_QUALITY;
  const fm = format ? String(format).toLowerCase() : DEFAULT_CONTENTFUL_FORMAT;
  const fitValue = fit ? String(fit).toLowerCase() : undefined;
  const focusValue = focus || undefined;

  const baseParams = {
    ...(fitValue ? {fit: fitValue} : null),
    ...(focusValue && fitValue && ['fill', 'crop', 'thumb'].includes(fitValue)
      ? {f: focusValue}
      : null),
    fm,
    q,
  };

  const displayW = Number(width);
  const displayH = Number(height);
  const hasDisplayW = Number.isFinite(displayW) && displayW > 0;
  const hasDisplayH = Number.isFinite(displayH) && displayH > 0;

  const src = buildContentfulImageUrl(imageUrl, {
    ...baseParams,
    ...(hasDisplayW ? {w: Math.round(displayW)} : null),
    ...(hasDisplayH ? {h: Math.round(displayH)} : null),
  });

  const widths = resolveSrcSetWidths({
    srcSetWidths,
    srcSetMultipliers,
    authoredWidth,
    displayWidth: hasDisplayW ? displayW : undefined,
    maxWidth,
  });

  const srcSet = widths
    .map((candidateW) => {
      const params = {...baseParams, w: Math.min(candidateW, CONTENTFUL_MAX_DIMENSION)};

      // Scale height with width when the author set both (crop/fill aspect).
      if (hasDisplayW && hasDisplayH) {
        params.h = Math.min(
          CONTENTFUL_MAX_DIMENSION,
          Math.max(1, Math.round((candidateW * displayH) / displayW))
        );
      } else if (hasDisplayH && !hasDisplayW) {
        params.h = Math.min(CONTENTFUL_MAX_DIMENSION, Math.round(displayH));
      }

      return `${buildContentfulImageUrl(imageUrl, params)} ${candidateW}w`;
    })
    .join(', ');

  let sizes = sizesOverride;
  if (!sizes) {
    sizes = hasDisplayW ? `${Math.round(displayW)}px` : '100vw';
  }

  return {src, srcSet, sizes};
}
